import { useCallback } from 'react';
import { useToast } from './useToast';

export interface InvalidatableQuery {
  invalidate: () => Promise<unknown>;
}

export interface MutationFeedbackOptions<TData = unknown, TError = unknown> {
  /**
   * Toast message displayed on success.
   * Can be a static string or a dynamic function `(data) => string`.
   */
  successMessage?: string | ((data: TData) => string);

  /**
   * Toast message displayed on failure.
   * Defaults to `error.message` if not provided.
   */
  errorMessage?: string | ((error: TError) => string);

  /**
   * Array of query invalidation handles (e.g. `utils.users.me` or `() => utils.invalidate()`)
   * to automatically trigger upon mutation success.
   */
  invalidateQueries?: Array<InvalidatableQuery | (() => Promise<unknown> | unknown)>;

  /** Optional custom onSuccess callback. */
  onSuccess?: (data: TData) => void;

  /** Optional custom onError callback. */
  onError?: (error: TError) => void;
}

/**
 * Custom hook providing a standardized mutation feedback pattern across all components.
 * Returns `onSuccess` and `onError` handlers suitable for tRPC or TanStack Query `useMutation`.
 *
 * @example
 * const updateProfileMutation = trpc.users.updateProfile.useMutation(
 *   useMutationFeedback({
 *     successMessage: 'Profile saved successfully!',
 *     invalidateQueries: [utils.users.me],
 *   })
 * );
 */
export function useMutationFeedback<TData = unknown, TError = unknown>(
  options: MutationFeedbackOptions<TData, TError> = {}
) {
  const { toastSuccess, toastError } = useToast();

  const handleSuccess = useCallback(
    async (data: TData) => {
      if (options.successMessage) {
        const msg =
          typeof options.successMessage === 'function'
            ? options.successMessage(data)
            : options.successMessage;
        toastSuccess(msg);
      }

      if (options.invalidateQueries && options.invalidateQueries.length > 0) {
        for (const queryHandle of options.invalidateQueries) {
          try {
            if (
              queryHandle &&
              (typeof queryHandle === 'object' || typeof queryHandle === 'function') &&
              'invalidate' in queryHandle &&
              typeof (queryHandle as { invalidate?: unknown }).invalidate === 'function'
            ) {
              await (queryHandle as { invalidate: () => Promise<unknown> }).invalidate();
            } else if (typeof queryHandle === 'function') {
              await (queryHandle as () => Promise<unknown> | unknown)();
            }
          } catch (err) {
            console.error('[useMutationFeedback] Invalidation failed:', err);
          }
        }
      }

      options.onSuccess?.(data);
    },
    [options, toastSuccess]
  );

  const handleError = useCallback(
    (error: TError) => {
      const defaultMsg =
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
          ? error.message
          : 'An error occurred while saving';

      const msg =
        typeof options.errorMessage === 'function'
          ? options.errorMessage(error)
          : options.errorMessage || defaultMsg;

      toastError(msg);
      options.onError?.(error);
    },
    [options, toastError]
  );

  return {
    onSuccess: handleSuccess,
    onError: handleError,
  };
}
